/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoUserSession,
  IAuthenticationDetailsData,
  CognitoIdToken,
} from 'amazon-cognito-identity-js';
import { environment } from '@env/environment';
import { Logger } from '@app/services/logger/logger.service';
import * as uuid from '@base/node_modules/uuid';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { OrganisationModel } from '@app/models/organisation.model';
import { AuthSessionRecorderService } from '@app/services/authentication/auth-session-recorder-service';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
import { SettingsService } from '@app/services/api-services/settings/settings.service';
import { OrganisationCognitoUser } from '@app/models/cognito-organisation-user.model';
import { MyPermissionsModel } from '@app/models/my-permissions.model';
import { Router } from '@angular/router';
import * as AWS from '@base/node_modules/aws-sdk';
import { switchMap } from 'rxjs/operators';
import { MaterialSnackbarService } from '@app/services/shared-services/material-snackbar.service';

const log = new Logger('AuthenticationService');

import { detect } from 'detect-browser';
import jwt_decode from 'jwt-decode';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { OrganisationUserService } from '@app/services/api-services/organisation-users/organisation-user.service';
const browser = detect();

export const MFA_REQUIRED = 'mfaRequired';
export const NEW_PASSWORD_REQUIRED = 'newPasswordRequired';
export const PASSWORD_RESET_REQUIRED = 'passwordResetRequired';

const poolData = {
  UserPoolId: environment.cognitoSettings.userPoolId,
  ClientId: environment.cognitoSettings.clientId,
};

const userPool = new CognitoUserPool(poolData);
const credentialsKey = 'credentials';
const userKey = 'userData';
const organisationKey = 'organisationData';
const permissionsKey = 'userPermissions';
const accessCredentials = 'accessCredentials';
const sessionSecret = 'secret';
const settingsKey = 'settings';

export interface Credentials {
  userId?: string;
  userName?: string;
  sessionId?: string;
  refreshToken?: string;
  accessToken?: string;
  idToken?: string;
}

export interface LoginContext {
  Username: string;
  Password: string;
}

export interface MFAContext {
  authenticationCode: string;
  remember: boolean;
}

// used for updating cognito user when first logging in
export interface DataUpdateContext {
  displayName?: string;
  name?: string;
  familyName?: string;
  telephone?: string;
  email?: string;
  newPassword: string;
  confirmPassword: string;
}

export interface RequiredDataUpdateContext {
  displayName?: string;
  nickname?: string;
  name?: string;
  family_name?: string;
  phone_number?: string;
  email?: string;
}

export interface Response {
  userId?: string;
  userName?: string;
  sessionId?: string;
  action?: string;
  success?: boolean;
  message?: string;
  userAttributes?: any;
  requiredAttributes?: any;
}

export enum supportedBrowserVersions {
  'safari' = 16,
  'firefox' = 106,
  'chrome' = 107,
  'edge-chromium' = 107,
}
@Injectable()
export class AuthenticationService {
  private userSubject = new ReplaySubject<OrganisationCognitoUser>(1);
  public user$ = this.userSubject.asObservable();
  private organisationSubject = new ReplaySubject<OrganisationModel>(1);
  public organisation$ = this.organisationSubject.asObservable();
  private permissionsSubject = new ReplaySubject<Array<MyPermissionsModel>>(1);
  public permissions$ = this.permissionsSubject.asObservable();
  private _requiredAttributes: any;
  private _sessionId: string;
  private _previousSessionId: string;
  public permissionsArray: string[] = []

  accessCredentials: AWS.Credentials;

  // Subscriptions
  private getCognitoUserSubscription: Subscription;
  private setDBSessionSubscription: Subscription;
  private logoutSubscription: Subscription;

  //okta
  okta = false;

  constructor(
    private authUserService: AuthUserService,
    private authSessionRecorderService: AuthSessionRecorderService,
    private router: Router,
    private notify: MaterialSnackbarService,
    private settingsService: SettingsService,
    protected http: HttpClient,
    private organisationUserService: OrganisationUserService
  ) {
    this.restoreSession();
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this.authUserService.credentials;
  }

  /**
   * Sets the user credentials.
   * @void
   */
  set credentials(credentials) {
    this.authUserService.credentials = credentials;
  }

  get organisation(): OrganisationModel {
    return this.authUserService.organisation;
  }

  set organisation(value: OrganisationModel) {
    this.authUserService.organisation = value;
  }

  get user(): OrganisationCognitoUser {
    return this.authUserService.user;
  }

  set user(value: OrganisationCognitoUser) {
    this.authUserService.user = value;
  }

  get permissions(): MyPermissionsModel[] {
    return this.authUserService.userPermissions;
  }

  set permissions(value: MyPermissionsModel[]) {
    this.authUserService.userPermissions = value;
    if (value) {
      this.storeSession(true);
    }
  }

  hasPermission(className: string, actionKey: string) {
    let hasPermission = false;
    if (this.permissions) {
      for (const permission of this.permissions) {
        if (
          permission.componentClass === className &&
          permission.isEnabled &&
          permission.componentActionKey === actionKey
        ) {
          hasPermission = true;
          break;
        }
      }
    }
    return hasPermission;
  }

  get requiredAttributes(): any {
    return this._requiredAttributes;
  }

  set requiredAttributes(value: any) {
    this._requiredAttributes = value;
  }

  get cognitoUser(): CognitoUser {
    return this.authUserService.cognitoUser;
  }

  set cognitoUser(value: CognitoUser) {
    this.authUserService.cognitoUser = value;
  }

  get cognitoSession(): CognitoUserSession {
    return this.authUserService.cognitoSession;
  }

  set cognitoSession(value: CognitoUserSession) {
    this.authUserService.cognitoSession = value;
  }

  setUserFromOrganisationUser(organisationUser: OrganisationUsersModel) {
    this.user.organisationId = organisationUser.organisationId;
    this.user.organisationRoleId = organisationUser.roleId;
    this.user.runAnalytics = organisationUser.runAnalytics;
    this.storeSession(true);
    this.userSubject.next(this.user);
  }

  setOrganisation(organisation: OrganisationModel) {
    this.organisation = organisation;
    this.storeSession(true);
    this.organisationSubject.next(this.organisation);
  }

  get sessionId(): string {
    return this._sessionId;
  }

  set sessionId(value: string) {
    this._sessionId = value;
  }

  get previousSessionId(): string {
    return this._previousSessionId;
  }

  set previousSessionId(value: string) {
    this._previousSessionId = value;
  }

  /**
   *  Ryan Duffy
   *
   *  restoreSession
   *
   *  The below sets up the session using a session from sessionStorage,
   *  it also can determine if a new tab is opened and duplicate the session if so,
   *  sessions must either timeout or be logged out to be killed, including a login from another location
   *
   * @tutorial https://blog.guya.net/2015/06/12/sharing-sessionstorage-between-tabs-for-secure-multi-tab-authentication/
   *
   * @memberof AuthenticationService
   */
  private restoreSession(): void {
    // Tested the previous configuration heavily and new Tabs does not retain session Storage
    // The below transfers the session into new Tabs
    // In the instance the window is not a new tab - set the sessionCredentials as normal
    let sessionCredentials = sessionStorage.getItem(credentialsKey) || null;
    let sessionUser = sessionStorage.getItem(userKey) || null;
    let sessionOrganisation = sessionStorage.getItem(organisationKey) || null;
    let sessionPermissions = sessionStorage.getItem(permissionsKey) || null;
    let accessKeys = sessionStorage.getItem(accessCredentials) || null;
    let secret = sessionStorage.getItem(sessionSecret) || null;
    let settings = sessionStorage.getItem(settingsKey) || null;
    let localSession =
      localStorage != null ? JSON.parse(localStorage.getItem('sessionStorage')) : null;

    if (sessionStorage.length === 0 && localSession != null) {
      sessionCredentials = localSession.credentials;
      sessionUser = localSession.userData;
      sessionPermissions = localSession.userPermissions;
      accessKeys = localSession.accessCredentials;
      secret = localSession.secret;
      settings = localSession.settings;
    }
    if (sessionCredentials) {
      try {
        this.credentials = JSON.parse(sessionCredentials);
        this.accessCredentials = JSON.parse(accessKeys);
        const identities = jwt_decode(this.credentials.idToken);
        if (identities['identities'] && identities['identities'].length > 0) {
          this.okta = true;
        }
        if (
          new Date(this.accessCredentials.expireTime).getTime() - 1 * 1 * 60 * 1000 <
            new Date().getTime() ||
          settings == null
        ) {
          log.error(`Error restoring this user session. Access tokens timeout.`);
          this.logoutSubscription = this.logout().subscribe(() => {
            this.router.navigate(['/login'], {
              queryParams: {
                reason: 'Session Timeout',
              },
            });
            if (this.logoutSubscription) {
              this.logoutSubscription.unsubscribe();
            }
            return;
          });
        } else {
          this.accessCredentials.secretAccessKey = JSON.parse(secret);
          this.cognitoUser = this.createCognitoUser(this.credentials.userName.toLocaleLowerCase());
          this.cognitoSession = new CognitoUserSession({
            IdToken: new CognitoIdToken({ IdToken: this.credentials.idToken.toString() }),
            AccessToken: new CognitoAccessToken({
              AccessToken: this.credentials.accessToken.toString(),
            }),
            RefreshToken: new CognitoRefreshToken({
              RefreshToken: this.credentials.refreshToken.toString(),
            }),
          });
          this.previousSessionId = this.sessionId = this.credentials.sessionId;
          this.cognitoUser.setSignInUserSession(this.cognitoSession);
          if (sessionUser) {
            this.user = new OrganisationCognitoUser();
            this.user = this.user.fromJson(JSON.parse(sessionUser));
            this.userSubject.next(this.user);
          }
          if (sessionOrganisation) {
            this.organisation = new OrganisationModel();
            this.organisation = this.organisation.fromJson(JSON.parse(sessionOrganisation));
          }
          if (sessionPermissions) {
            const permission = new MyPermissionsModel();
            for (const item of JSON.parse(sessionPermissions)) {
              this.permissions.push(permission.fromJson(item));
            }
            this.permissionsSubject.next(this.permissions);
          }
          if (settings) {
            this.settingsService.setSettings(JSON.parse(settings));
          }
          if (sessionCredentials) {
            this.storeSession(true);
          }
          // If a tab has no sessiondetails, set and item to ask other tabs for session storage
          if (!sessionStorage.length) {
            localStorage.setItem('getSessionStorage', new Date().toString());
          }
          // Listen for an storage event on each of the windows
          window.addEventListener('storage', (event) => {
            if (event.key === 'getSessionStorage') {
              // Some tab asked for the sessionStorage -> send it
              // If the getSessionStorage key is found, temporarily store session details in local storage.
              // Immediately remove the details from localStorage so it is only found in the session
              localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
              localStorage.removeItem('sessionStorage');
            } else if (event.key === 'sessionStorage' && !sessionStorage.length) {
              // sessionStorage is empty, fill it with the details found in the temporary local data
              const data = JSON.parse(event.newValue) || null;
              if (data) {
                // Set the separate items
                sessionStorage.setItem(credentialsKey, data[credentialsKey]);
                sessionStorage.setItem(userKey, data[userKey]);
                sessionStorage.setItem(organisationKey, data[organisationKey]);
                sessionCredentials = sessionStorage.getItem(credentialsKey) || null;
                sessionUser = sessionStorage.getItem(userKey) || null;
                sessionOrganisation = sessionStorage.getItem(organisationKey) || null;
                // Set the credentials for the new tab
                if (sessionCredentials) {
                  this.credentials = JSON.parse(sessionCredentials);
                  this.cognitoUser = this.createCognitoUser(
                    this.credentials.userName.toLocaleLowerCase()
                  );

                  this.cognitoSession = new CognitoUserSession({
                    IdToken: new CognitoIdToken({ IdToken: this.credentials.idToken.toString() }),
                    AccessToken: new CognitoAccessToken({
                      AccessToken: this.credentials.accessToken.toString(),
                    }),
                    RefreshToken: new CognitoRefreshToken({
                      RefreshToken: this.credentials.refreshToken.toString(),
                    }),
                  });
                  this.previousSessionId = this.sessionId = this.credentials.sessionId;
                  this.cognitoUser.setSignInUserSession(this.cognitoSession);
                  this.getSession();
                }
                if (sessionUser) {
                  this.user = new OrganisationCognitoUser();
                  this.user = this.user.fromJson(JSON.parse(sessionUser));
                  this.userSubject.next(this.user);
                }
                if (sessionOrganisation) {
                  this.organisation = new OrganisationModel();
                  this.organisation = this.organisation.fromJson(JSON.parse(sessionOrganisation));
                  this.organisationSubject.next(this.organisation);
                }
                if (sessionPermissions) {
                  const permission = new MyPermissionsModel();
                  for (const item of JSON.parse(sessionPermissions)) {
                    this.permissions.push(permission.fromJson(item));
                  }
                  this.permissionsSubject.next(this.permissions);
                }
                if (sessionCredentials) {
                  this.storeSession(true);
                }
              }
            }
          });
        }
      } catch (e) {
        log.error(`Error restoring this user session. Error response received: ${e}`);
        this.logoutSubscription = this.logout().subscribe(() => {
          this.router.navigate(['/login'], {
            queryParams: {
              reason: 'Session Could not be restored',
            },
            replaceUrl: true,
          });
          if (this.logoutSubscription) {
            this.logoutSubscription.unsubscribe();
          }
        });
      }
    }
  }

  /**
   *  Warren Dowey
   *
   *  login
   *
   *  Tries to log a user in with supllied username and password
   *
   * @param context The login parameters.
   *
   * @return The user credentials.as an observable of type any
   */
  login(context: IAuthenticationDetailsData): Observable<any> {
    let response: Response;
    const authenticationDetails = new AuthenticationDetails(context);
    this.cognitoUser = this.createCognitoUser(context.Username.toLocaleLowerCase());
    return new Observable((observer) => {
      {
        this.cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            this.cognitoSession = result;
            // Set the new SessionID
            this.sessionId = uuid.v4();
            response = {
              userId: result.getAccessToken().payload.sub,
              userName: context.Username,
              sessionId: this._sessionId,
              success: true,
            };
            this.credentials = {
              userId: result.getAccessToken().payload.sub,
              userName: context.Username,
              sessionId: this.sessionId,
              accessToken: result.getAccessToken().getJwtToken(),
              idToken: result.getIdToken().getJwtToken(),
              refreshToken: result.getRefreshToken().getToken(),
            };
            this.user = new OrganisationCognitoUser();
            this.user.username = context.Username;
            this.user.userId = result.getAccessToken().payload.sub;

            this.cognitoUser.setSignInUserSession(result);
            this.getCognitoUserSubscription = this.getCognitoUser()
              .pipe(
                switchMap(() => {
                  return this.setAccessCredentials(this.credentials.idToken);
                })
              )
              .subscribe((accessCreds: AWS.Credentials) => {
                this.accessCredentials = accessCreds;
                if (this.getCognitoUserSubscription) {
                  this.getCognitoUserSubscription.unsubscribe();
                }
                this.storeSession(true);
                this.setDBSessionSubscription = this.setDBSession(
                  result.getAccessToken().payload.sub,
                  this.sessionId
                ).subscribe(() => {
                  if (this.setDBSessionSubscription) {
                    this.setDBSessionSubscription.unsubscribe();
                  }
                  observer.next(response);
                  observer.complete();
                });
              });
          },
          onFailure: (err) => {
            if (err.code === 'PasswordResetRequiredException') {
              response = {
                success: false,
                message: err.message,
                action: PASSWORD_RESET_REQUIRED,
              };
            } else {
              response = {
                success: false,
                message: err.message,
                action: 'none',
              };
            }
            observer.next(response);
            observer.complete();
          },
          mfaRequired: (codeDeliveryDetails) => {
            response = {
              success: false,
              action: MFA_REQUIRED,
            };
            observer.next(response);
            observer.complete();
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            response = {
              success: false,
              action: NEW_PASSWORD_REQUIRED,
              userAttributes: userAttributes,
              requiredAttributes: requiredAttributes,
            };
            this._requiredAttributes = requiredAttributes;
            observer.next(response);
            observer.complete();
          },
        });
      }
    });
  }

  oktaLogin(
    tokens: { access_token: string; id_token: string; refresh_token: string },
    user: any
  ): Observable<any> {
    return new Observable<any>((subscriber) => {
      this.sessionId = uuid.v4();
      this.credentials = {
        userId: user.sub,
        userName: user.username,
        sessionId: this.sessionId,
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      };
      this.user = new OrganisationCognitoUser();
      this.user.username = user.username;
      this.user.userId = user.sub;
      this.getCognitoUser().subscribe();
      this.setAccessCredentials(this.credentials.idToken).subscribe(
        (accessCreds: AWS.Credentials) => {
          this.accessCredentials = accessCreds;
          if (this.getCognitoUserSubscription) {
            this.getCognitoUserSubscription.unsubscribe();
          }
          this.storeSession(true);
          this.setDBSessionSubscription = this.setDBSession(user.sub, this.sessionId).subscribe(
            () => {
              if (this.setDBSessionSubscription) {
                this.setDBSessionSubscription.unsubscribe();
              }
            }
          );
        }
      );
      subscriber.next();
    });
  }
  /**
   *
   *
   * @param {string} idToken
   * @returns {Observable<AWS.Credentials>}
   * @memberof AuthenticationService
   */
  setAccessCredentials(idToken?: string): Observable<AWS.Credentials> {
    const cognitoidentity = new AWS.CognitoIdentity();
    const providerName = `cognito-idp.${environment.s3Region}.amazonaws.com/${environment.cognitoSettings.userPoolId}`;
    let identityId: string;
    return new Observable<AWS.Credentials>((observer) => {
      cognitoidentity.getId(
        {
          IdentityPoolId: environment.cognitoSettings.identityPoolId,
          Logins: {
            [providerName]: idToken,
          },
        },
        (err, data) => {
          if (err) {
            log.error(err);
            observer.error(err);
          } else {
            identityId = data.IdentityId;
            cognitoidentity.getCredentialsForIdentity(
              {
                IdentityId: identityId,
                Logins: {
                  [providerName]: idToken,
                },
              },
              (err, data) => {
                if (err) {
                  log.error(err);
                  observer.error(err);
                } else {
                  const creds = new AWS.Credentials(
                    data.Credentials.AccessKeyId,
                    data.Credentials.SecretKey,
                    data.Credentials.SessionToken
                  );
                  creds.expireTime = data.Credentials.Expiration;
                  observer.next(creds);
                }
              }
            );
          }
        }
      );
    });
  }

  /**
   *  Warren Dowey
   *
   *  mfaChallenge
   *
   *  Challenges the user to enter the code they received from their MFA provider
   *
   * @param context in the format of Credentials
   *
   * @return returns an observable that completes by either sending an MFA code and taking the entered
   * code successfully or returning an error
   */
  mfaChallenge(context: MFAContext): Observable<Credentials> {
    let response;
    return new Observable((observer) => {
      this.cognitoUser.sendMFACode(context.authenticationCode, {
        onSuccess: (result) => {
          this.cognitoSession = result;
          // Set the new SessionID
          this.sessionId = uuid.v4();
          response = {
            userId: result.getAccessToken().payload.sub,
            userName: this.cognitoUser.getUsername(),
            sessionId: this._sessionId,
            success: true,
          };
          this.credentials = {
            userId: result.getAccessToken().payload.sub,
            userName: this.cognitoUser.getUsername(),
            sessionId: this.sessionId,
            accessToken: result.getAccessToken().getJwtToken(),
            idToken: result.getIdToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken(),
          };
          this.user = new OrganisationCognitoUser();
          this.user.userId = result.getAccessToken().payload.sub;
          this.cognitoUser.setSignInUserSession(result);
          this.getCognitoUserSubscription = this.getCognitoUser()
            .pipe(
              switchMap(() => {
                return this.setAccessCredentials(this.credentials.idToken);
              })
            )
            .subscribe((accessCreds: AWS.Credentials) => {
              this.accessCredentials = accessCreds;
              if (this.getCognitoUserSubscription) {
                this.getCognitoUserSubscription.unsubscribe();
              }
              this.storeSession(true);
              this.setDBSessionSubscription = this.setDBSession(
                result.getAccessToken().payload.sub,
                this.sessionId
              ).subscribe(() => {
                if (this.setDBSessionSubscription) {
                  this.setDBSessionSubscription.unsubscribe();
                }
                observer.next(response);
                observer.complete();
              });
            });
        },
        onFailure: (err) => {
          observer.error(err);
          observer.complete();
        },
      });
    });
  }
  /**
   *  Warren Dowey
   *
   *  newPasswordChallenge
   *
   *  Challenges the user to enter their details after they are required to setup a new password
   *
   * @param context in the format of DataUpdateContext
   *
   * @return returns an observable that completes by either resetting the password and redirecting or errors
   */
  newPasswordChallenge(context: DataUpdateContext): Observable<any> {
    let response: Response;
    const newPassword = context.newPassword;
    const requiredAttributeData: RequiredDataUpdateContext = {};
    return new Observable((observer) => {
      for (const attribute of this._requiredAttributes) {
        if (attribute === 'nickname') {
          requiredAttributeData.nickname = context.displayName;
        }
        if (attribute === 'name') {
          requiredAttributeData.name = context.name;
        }
        if (attribute === 'family_name') {
          requiredAttributeData.family_name = context.familyName;
        }
        if (attribute === 'phone_number') {
          requiredAttributeData.phone_number = context.telephone;
        }
        if (attribute === 'email') {
          requiredAttributeData.email = context.email;
        }
      }
      this.cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributeData, {
        onSuccess: (result) => {
          this.cognitoSession = result;
          // Set the new SessionID
          this.sessionId = uuid.v4();
          response = {
            userId: result.getAccessToken().payload.sub,
            userName: this.cognitoUser.getUsername(),
            sessionId: this._sessionId,
            success: true,
          };
          this.credentials = {
            userId: result.getAccessToken().payload.sub,
            userName: this.cognitoUser.getUsername(),
            sessionId: this.sessionId,
            accessToken: result.getAccessToken().getJwtToken(),
            idToken: result.getIdToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken(),
          };
          this.user = new OrganisationCognitoUser();
          this.user.userId = result.getAccessToken().payload.sub;
          this.cognitoUser.setSignInUserSession(result);
          this.getCognitoUserSubscription = this.getCognitoUser()
            .pipe(
              switchMap(() => {
                return this.setAccessCredentials(this.credentials.idToken);
              })
            )
            .subscribe((accessCreds: AWS.Credentials) => {
              this.accessCredentials = accessCreds;
              if (this.getCognitoUserSubscription) {
                this.getCognitoUserSubscription.unsubscribe();
              }
              this.storeSession(true);
              this.setDBSessionSubscription = this.setDBSession(
                result.getAccessToken().payload.sub,
                this.sessionId
              ).subscribe(() => {
                if (this.setDBSessionSubscription) {
                  this.setDBSessionSubscription.unsubscribe();
                }
                observer.next(response);
                observer.complete();
              });
            });
        },
        onFailure: (error) => {
          response = {
            success: false,
            message: error.message,
          };
          observer.error(response);
          observer.complete();
        },
      });
    });
  }

  /**
   *  Warren Dowey
   *
   *  createCognitoUser
   *
   *  Creates a new cognito user based on their email from the userpool.
   *
   * @param email of the user as a string
   *
   * @return  the newly created CognitoUser in the format of CognitoUser
   */
  createCognitoUser(email: string): CognitoUser {
    const userData = {
      Username: email.toLocaleLowerCase(),
      Pool: userPool,
    };
    return new CognitoUser(userData);
  }

  /**
   *  cognitoUserRefreshSession
   *
   *  refresh the current user's session
   *
   *  Ryan Duffy
   *
   * @memberof AuthenticationService
   */
  cognitoUserRefreshSession() {
    const refreshToken = new CognitoRefreshToken({ RefreshToken: this.credentials.refreshToken });
    if (this.cognitoUser && !this.okta) {
      // Refresh the token for the current cognitoUser
      this.cognitoUser.refreshSession(refreshToken, (refreshError, refreshedSession) => {
        if (refreshError) {
          log.debug('refreshing token error');
          throw refreshError;
        } else {
          // Set the credentials with the refreshed token so the user stays logged in
          this.credentials = {
            userId: this.credentials.userId,
            userName: this.credentials.userName,
            sessionId: this.credentials.sessionId,
            idToken: refreshedSession.getIdToken().getJwtToken(),
            refreshToken: refreshedSession.getRefreshToken().getToken(),
            accessToken: refreshedSession.getAccessToken().getJwtToken(),
          };
          this.cognitoSession = new CognitoUserSession({
            IdToken: new CognitoIdToken({ IdToken: this.credentials.idToken.toString() }),
            AccessToken: new CognitoAccessToken({
              AccessToken: this.credentials.accessToken.toString(),
            }),
            RefreshToken: new CognitoRefreshToken({
              RefreshToken: this.credentials.refreshToken.toString(),
            }),
          });
          this.setAccessCredentials(this.credentials.idToken).subscribe(
            (creds: AWS.Credentials) => {
              log.debug('refreshing token success');
              this.accessCredentials = creds;
              this.storeSession(true);
            }
          );
        }
      });
    } else if (this.okta) {
      this.oktaCheck(
        this.credentials.refreshToken,
        'refresh_token',
        environment.oktaSettings.clientId,
        'refresh_token'
      ).subscribe();
    }
  }

  /**
   *  Ryan Duffy
   *
   *  Setup the user's session inside the database so it can be used to keep track of the current session
   *
   * @param {string} userId
   * @param {string} sessionId
   * @returns {Observable<any>}
   * @memberof AuthenticationService
   */
  setDBSession(userId: string, sessionId: string): Observable<any> {
    return new Observable((observer) => {
      this.authSessionRecorderService.recordSession(sessionId, userId).subscribe(() => {
        observer.next();
        observer.complete();
      });
    });
  }
  /**
   *  Warren Dowey
   *
   *  getSession
   *
   *  Get the session of the currently signed in user
   *
   * @param none
   *
   * @return gets the current user or their session
   */
  getSession() {
    if (this.cognitoUser != null) {
      this.cognitoUser.getSession((error, session) => {
        if (error) {
          return;
        } else {
          this.getCognitoUser();
          return session;
        }
      });
    }
  }

  /**
   *  Warren Dowey
   *
   *  logout
   *
   *  Logs out the user and clear credentials.
   *
   * @param none
   *
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    // Clear the current database record for the sessionID and update the timestamp
    // and following that clear the session details
    return new Observable<boolean>((observer) => {
      if (this.user) {
        this.setDBSessionSubscription = this.setDBSession(this.user.userId, null).subscribe(
          () => {
            this.storeSession(false);
            try {
              this.getAuthenticatedUser().signOut();
            } catch (e) {
              log.debug('User already logged out');
            }
            observer.next(true);
          },
          () => {}
        );
      } else {
        this.storeSession(false);
        try {
          this.getAuthenticatedUser().signOut();
        } catch (e) {
          log.debug('User already logged out');
        }
        observer.next(true);
      }
    });
  }

  /**
   *  Warren Dowey
   *
   *  isAuthenticated
   *
   *  Checks is the user is authenticated.
   *
   * @param void
   *
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    const date = new Date();
    const time = date.getTime();

    if (!this.credentials) {
      return false;
    } else {
      if (!this.user) {
        return false;
      }
      if (this.cognitoSession) {
        if (this.cognitoSession.getAccessToken().getExpiration() * 1000 > time) {
          return userPool.getCurrentUser() != null;
        } else {
          this.logout().subscribe(
            () => {},
            () => {}
          );
          return false;
        }
      } else {
        return true;
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  getAuthenticatedUser
   *
   *  Gets the currently authenticated user
   *
   * @param none
   *
   * @returns: null or the the userPool.getCurrentUser();
   */
  getAuthenticatedUser() {
    if (!this.credentials) {
      return null;
    }
    // gets the current user from the local storage
    return userPool.getCurrentUser();
  }

  /**
   *  Warren Dowey
   *
   *  storeSession
   *
   *  Store the user credentials to session storage or clears them.
   *
   * @param store: boolean true to store or defaults to false to clear.
   *
   * @returns: Void: sets the user credentials
   */
  storeSession(store = false) {
    const permissions: Object[] = [];
    if (this.credentials && this.accessCredentials) {
      if (this.credentials && store) {
        sessionStorage.setItem(credentialsKey, JSON.stringify(this.credentials));
        sessionStorage.setItem(accessCredentials, JSON.stringify(this.accessCredentials));
        sessionStorage.setItem(
          sessionSecret,
          JSON.stringify(this.accessCredentials.secretAccessKey)
        );
        if (this.user) {
          sessionStorage.setItem(userKey, JSON.stringify(this.user.toJson()));
        }
        if (this.organisation) {
          sessionStorage.setItem(organisationKey, JSON.stringify(this.organisation.toJson()));
        }
        if (this.permissions) {
          for (const item of this.permissions) {
            permissions.push(item.toJson());
          }
          sessionStorage.setItem(permissionsKey, JSON.stringify(permissions));
        }
        localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
        this.getCognitoUser();
      } else {
        this.cognitoUser = null;
        this.user = null;
        this.organisation = null;
        this.permissions = null;
        this.credentials = null;
        this.okta = null;
        this.accessCredentials = null;
        localStorage.clear();
        sessionStorage.clear();
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  getCognitoUser
   *
   *  get the current Cognito User
   *
   * @param data the user to be setup
   *
   * @returns: Observable of type any that contains the details of the user.
   */
  getCognitoUser(): Observable<any> {
    return new Observable((observer) => {
      AWS.config.region = environment.cognitoSettings.region; // Region
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoSettings.identityPoolId,
      });
      const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider();
      const params = {
        AccessToken: this.credentials.accessToken /* required */,
      };
      cognitoIdentityServiceProvider.getUser(params, (err, data) => {
        if (err) {
          this.logout().subscribe();
          observer.error(err);
          observer.complete();
        } else {
          this.setupUser(data);
          observer.next(data);
          observer.complete();
        }
      });
    });
  }

  /**
   *  Warren Dowey
   *
   *  setupUser
   *
   *  used with the cognito service to setup a user if they aren't already
   *
   * @param data the user to be setup
   *
   * @returns: void: assigns the new user details to the user object
   *
   */
  setupUser(data: any): void {
    if (!this.user) {
      this.user = new OrganisationCognitoUser();
    }
    if (data['MFAOptions']) {
      const MFA = data['MFAOptions'];
      this.user.mfaEnabled = true;
    } else {
      this.user.mfaEnabled = false;
    }
    this.user.username = data['Username'];
    const attributes = data['UserAttributes'];
    for (const item of attributes) {
      switch (item['Name']) {
        case 'sub': {
          this.user.userId = item['Value'];
          break;
        }
        case 'name': {
          this.user.firstName = item['Value'];
          break;
        }
        case 'family_name': {
          this.user.familyName = item['Value'];
          break;
        }
        case 'email': {
          this.user.email = item['Value'];
          break;
        }
        case 'phone_number': {
          this.user.phoneNumber = item['Value'];
          break;
        }
        case 'email_verified': {
          this.user.emailVerified = item['Value'] === 'true';
          break;
        }
        case 'phone_number_verified': {
          this.user.phoneNumberVerified = item['Value'] === 'true';
          break;
        }
        case 'custom:display_picture': {
          this.user.displayPicture = item['Value'];
          break;
        }
        case 'custom:favourite_color': {
          this.user.favouriteColour = item['Value'];
        }
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  forgetDevice
   *
   *  update the current user's device remembered status to forgotten, so they have to do MFA again
   *
   * @param none
   *
   * @returns: void: updates the user's device remembered settings
   */
  rememberDevice(): Observable<any> {
    let response: Response;
    return new Observable((observer) => {
      this.cognitoUser.setDeviceStatusRemembered({
        onSuccess: (result) => {
          response.success = true;
          observer.next(response);
          observer.complete();
        },
        onFailure: (error) => {
          response = {
            success: false,
            message: error.message,
          };

          observer.next(response);
          observer.complete();
        },
      });
    });
  }

  checkBrowser() {
    let version = parseInt(browser.version);
    switch (browser.name) {
      case 'safari':
        if (version < supportedBrowserVersions.safari) {
          this.displayBrowserWarning();
        }
        break;
      case 'firefox':
        if (version < supportedBrowserVersions.firefox) {
          this.displayBrowserWarning();
        }
        break;
      case 'chrome':
        if (version < supportedBrowserVersions.chrome) {
          this.displayBrowserWarning();
        }
        break;
      case 'edge-chromium':
        if (version < supportedBrowserVersions['edge-chromium']) {
          this.displayBrowserWarning();
        }
        break;
    }
  }

  displayBrowserWarning() {
    // Check session storage to see if browser has already been checked
    if (!sessionStorage.getItem('browserChecked')) {
      // Set session storage so message isn't displayed multiple times
      sessionStorage.setItem('browserChecked', 'true');
      this.notify.openSnackBarPosition(
        'Warning your current browser version is not supported and may cause issues, please update your browser before continuing',
        'Close',
        5000,
        'top'
      );
    }
  }

  oktaCheck(token, tokenType?, clientId?, grantType?): Observable<OrganisationUsersModel> {
    return new Observable<OrganisationUsersModel>((subscriber) => {
      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded;',
        grant_type: grantType,
        client_id: clientId,
        redirect_uri: environment.oktaSettings.redirectUri,
        tokenType: token,
        cognitoEndpoint: environment.oktaSettings.cognitoEndpoint,
      });
      this.http
        .post(environment.oktaSettings.tokenUrl, '', { headers: headers })
        .subscribe((res) => {
          if (res) {
            if (!this.okta) {
              this.getOktaUserInfo(res).subscribe((info) => {
                subscriber.next(info);
              });
            }
          }
        });
    });
  }

  getOktaUserInfo(userInfo): Observable<any> {
    return new Observable<any>((subscriber) => {
      const headersAuth = new HttpHeaders({
        Authorization: 'Bearer ' + userInfo['access_token'],
      });
      this.http
        .post(environment.oktaSettings.userInfoUrl, null, { headers: headersAuth })
        .subscribe((authorised) => {
          // do db insert of user if authorised
          this.organisationUserService.createUser(authorised['sub']).subscribe((user) => {
            if (user) {
              this.oktaLogin(userInfo, authorised).subscribe(() => {
                this.okta = true;
                subscriber.next(user);
              });
            }
          });
        });
    });
  }

  getPermissionsArray(permissions: MyPermissionsModel[]): Observable<any> {
    return new Observable<any>((subscriber) => {
      if (this.permissionsArray.length === 0) {
        let permissionsArray = [];
        permissions.forEach((perm) => {
          permissionsArray.push(perm.componentActionKey);
        });
        this.permissionsArray = permissionsArray;
        subscriber.next(this.permissionsArray);
      } else {
        subscriber.next(this.permissionsArray);
      }
    });
  }

}
